import React, { Fragment, useEffect, useState, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { VIEW_PAGE_CONTENT_TOOLS_FORGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { FORGE_MODULE_CONTENT_ACTION } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { ContentActionForgeApp } from '@confluence/forge-ui/entry-points/apps.ts';
import { getContentType } from '@confluence/forge-ui/entry-points/contentType.ts';
import { getAppId, getModuleKey } from '@confluence/forge-ui/entry-points/appData.ts';
import { recordNonContentAppKeys } from '@confluence/browser-metrics/entry-points/NonContentAppKeys.ts';
import { fg } from '@confluence/feature-gating';
import { lazyForgeUIExtensionsWithLoader } from '@confluence/forge-ui/entry-points/lazy.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageState } from '@confluence/page-context';
import { getSpaceType } from '@confluence/space-utils/entry-points/getSpaceType.ts';
import { useSpaceIdentifiers } from '@confluence/space-utils/entry-points/useSpaceIdentifiers.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useMoreActionsMenuActions } from '@confluence/content-types-utils/entry-points/moreActionsMenuState.ts';

import { ForgeContentToolsItem } from './ForgeContentToolsItem.tsx';
import { ContentToolsButton } from './ContentToolsButton.tsx';

const EXPERIENCE_NAME = VIEW_PAGE_CONTENT_TOOLS_FORGE_EXPERIENCE;

type ForgeContentToolsProps = {
	contentId: string; // Current contentId
	spaceKey: string; // Current spaceKey
	children: (items: React.JSX.Element[]) => React.ReactNode; // Function that accepts forge menu items.
	onLoadComplete: () => void; // Triggered when the forge extension is loaded.
	isDropdownMenuOpen: boolean; // Boolean that indicates open/close status of containing dropdown menu.
	lazyLoaderPlaceholderComponent?: React.ReactNode; // If provided, this component will be rendered in the LazyLoader instead of the ContentToolsButton.
};
/**
 * Calls back children with the forge extension menu items. Also contains the logic of how to load selected
 * forge extension.
 */
export function ForgeContentTools({
	contentId,
	spaceKey,
	children,
	onLoadComplete,
	isDropdownMenuOpen,
	lazyLoaderPlaceholderComponent = null,
}: ForgeContentToolsProps): React.JSX.Element {
	const experienceTracker = useContext(ExperienceTrackerContext);
	// Forge menu items are behaving a little different than other menu items. They show a loading spinner if one of them
	// is clicked. There are a few different cases that needs to be handled while a forge extension is being loaded:
	// 1. Show a loading spinner while an extension id being loaded.
	//    - Stop showing the loading spinner once the extension is loaded.
	//    - Close the dropdown menu once the extension is loaded.
	// 2. If dropdown menu is closed while an extension is being loaded then stop loading the extension.
	// If there's a selected extension then `loadExtension` state changes like this:
	// +----------+----------------------+------------------+
	// |isLoading |   isDropdownMenuOpen |    loadExtension |
	// +----------------------------------------------------+
	// |          |                      |                  |
	// | false    |   false              |    true          |  --> keep showing the loaded extension
	// |          |                      |                  |
	// | false    |   true               |    true          |  --> keep showing the loaded extension
	// |          |                      |                  |
	// | true     |   false              |    false         |  --> stop loading the extension
	// |          |                      |                  |
	// | true     |   true               |    true          |  --> keep loading the extension
	// +----------+----------------------+------------------+
	const [extension, showExtension] = useState(null);
	const [isLoading, setLoading] = useState(false);
	const loadExtension = extension && !(isLoading && !isDropdownMenuOpen);

	useEffect(() => {
		if (!loadExtension) {
			experienceTracker.abort({
				name: EXPERIENCE_NAME,
				reason: 'Canceled by user',
			});
			setLoading(false);
			showExtension(null);
		}
	}, [loadExtension, experienceTracker]);

	const onLoadCompleteCB = useCallback(() => {
		onLoadComplete();
		setLoading(false);
	}, [onLoadComplete, setLoading]);

	const onCloseCB = useCallback(() => {
		showExtension(null);
	}, [showExtension]);

	const onForgeExtensionClick = useCallback(
		(ext: any) => {
			experienceTracker.start({
				name: EXPERIENCE_NAME,
				id: ext.id,
				timeout: 10000,
			});
			showExtension(ext);
			setLoading(true);
		},
		[showExtension, setLoading, experienceTracker],
	);

	// shows up when preloaded in SSR. Shows up again when SPA takes over bf all queries are loaded.
	const LazyLoader = useMemo(
		() =>
			lazyForgeUIExtensionsWithLoader(
				() =>
					lazyLoaderPlaceholderComponent ?? (
						<ContentToolsButton
							// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
							isDisabled={!(window.__SSR_RENDERED__ || process.env.REACT_SSR)}
						/>
					),
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	return (
		<Fragment>
			{loadExtension && (
				<ForgeUIExtensionLoader
					extension={extension}
					contentId={contentId}
					spaceKey={spaceKey}
					onLoadComplete={onLoadCompleteCB}
					onClose={onCloseCB}
				/>
			)}
			<LazyLoader
				moduleType={FORGE_MODULE_CONTENT_ACTION}
				render={(extensions, loading, error) => {
					if (loading) return children([]);

					if (error) {
						if (!isErrorMarkedAsHandled(error)) {
							getMonitoringClient().submitError(error, {
								attribution: Attribution.ECOSYSTEM,
							});
						}
						return children([]);
					}

					// Instrument the presence of non-content 3P content-action apps so
					// their keys are folded into the page-load `connectAppKeys` telemetry.
					if (fg('cc_record_non_content_macros')) {
						recordNonContentAppKeys(extensions.map((ext) => getAppId(ext)));
					}

					const result = extensions.map((ext) => (
						<ForgeContentToolsItem
							key={ext.id}
							extension={ext}
							onClick={onForgeExtensionClick}
							isLoading={extension === ext}
						/>
					));

					return children(result);
				}}
			/>
		</Fragment>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InvisibleDiv = styled.div({
	display: 'none',
});

/**
 * LazyForgeUIExtension has its own loading spinner, however, we don't want to show it anywhere. We want to show a
 * loading spinner inside the menu item while loading the extension, and then close the menu once the loading is complete.
 * To accomplish that, we're rendering LazyForgeUIExtension inside an invisible div.
 * @param forgeExtension To be loaded.
 * @param contentId Passed down to ForgeUIExtension both contentId and spaceKey becomes available in the forge app with
 * `useProductContext` hook. For reference https://developer.atlassian.com/platform/forge/ui-hooks-reference/.
 * @param spaceKey Passed down to ForgeUIExtension.
 * @param onLoadComplete Triggered when the forge extension is loaded.
 * @param onClose Triggered when the rendered ModalDialog is closed. (Currently ContentAction forge component can only contain a ModalDialog)
 * @returns LazyForgeUIExtension inside an InvisibleDiv.
 * @constructor
 */
function ForgeUIExtensionLoader({
	extension,
	contentId,
	spaceKey: spaceAlias,
	onLoadComplete,
	onClose,
}) {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [{ routeName }] = usePageState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activationId } = useSessionData();
	const { spaceId, spaceKey } = useSpaceIdentifiers();
	const isLivePage = useIsCurrentPageLive();
	const { setIsCloseMenuDisabled } = useMoreActionsMenuActions();

	useEffect(() => {
		// Ensures the MoreActions dialog remains opened while the Forge app is running
		// Without this, interactions with any portals from UI Kit components will close the menu which closes the Forge app
		setIsCloseMenuDisabled(true);
		return () => {
			setIsCloseMenuDisabled(false);
		};
	}, [setIsCloseMenuDisabled]);

	const onInitialRender = useCallback(() => {
		experienceTracker.succeed({
			name: EXPERIENCE_NAME,
		});
		onLoadComplete();

		const appId = getAppId(extension);
		const analyticEvent = createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'invoked',
				actionSubject: 'forgeExtension',
				actionSubjectId: appId,
				containerType: 'space',
				containerId: spaceId,
				objectType: getContentType(routeName),
				objectId: contentId,
				source: FORGE_MODULE_CONTENT_ACTION,
				attributes: {
					activationId,
					appId,
					moduleType: FORGE_MODULE_CONTENT_ACTION,
					moduleKey: getModuleKey(extension),
					spaceType: getSpaceType(spaceAlias),
					spaceId,
				},
			},
		});
		analyticEvent.fire();
	}, [
		onLoadComplete,
		experienceTracker,
		activationId,
		createAnalyticsEvent,
		contentId,
		spaceAlias,
		spaceId,
		extension,
		routeName,
	]);

	if (!spaceKey) {
		return null;
	}

	return (
		<InvisibleDiv>
			<ContentActionForgeApp
				// this key is required to enable user switch in between different forge apps during loading of an extension
				key={extension.id}
				app={extension}
				extensionData={{
					type: FORGE_MODULE_CONTENT_ACTION,
					// This ts ignore can be removed once the ff is cleaned up.
					// @ts-ignore FIXME `type` is declared by `extensionData` to be required for `content`
					content: {
						id: contentId,
						subtype: isLivePage ? 'live' : null,
						type: getContentType(routeName),
					},
					space: {
						key: spaceKey || spaceAlias,
						id: spaceId,
					},
				}}
				onInitialRender={onInitialRender}
				onTearDown={() => {
					setIsCloseMenuDisabled(false);
					onClose();
				}}
			/>
		</InvisibleDiv>
	);
}

ForgeUIExtensionLoader.propTypes = {
	extension: PropTypes.object.isRequired,
	contentId: PropTypes.string.isRequired,
	spaceKey: PropTypes.string.isRequired,
	onLoadComplete: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};
